// Catalog.ID Client — Organization key rotation, the pure half
// ============================================================================
// What an organization key rotation writes back into the ORG key bag.
//
// `POST /api/organization/{orgCid}/rotate` is DELETE-ALL + CREATE on the org
// bag (`DelegateController.RotateOrgKeys`), so the submitted bag is the whole
// truth: an entry the client forgets is destroyed, not merely stale.
//
// Defect G10 (found 2026-09-10, registration-restructure §16.6). The rotation
// used to rebuild the bag from the ROTATING DELEGATE'S WRAPPED COPIES
// (`getWrappedOrgKeys` → unwrap → bag). The server admits `admin` as well as
// `owner`, and `KnownKeyTypes.GetEntitledOrgKeys` gives an admin neither
// `main_signing_key` nor `xrpl_master`, so an admin-run rotation silently
// stripped both from the organization. `recovery_kit` made it fatal: it is
// wrapped to NOBODY by design, so the first rotation after an organization
// created a Recovery Kit would have erased the set behind every sheet already
// in a custodian's hands, with no way back.
//
// The fix is one sentence: the new bag is built from the DECRYPTED SERVER BAG,
// which is the organization's own record of what it holds, and the actor's
// entitlement decides only what gets RE-WRAPPED for delegates.

import type { KeyBag, KeyBagEntry } from '../crypto/keybag';
import { getEntitledOrgKeys } from '../state/org-key-entitlements';

/** The key ids an organization rotation replaces. Everything else is carried
 *  through untouched — chat/mail identity keys rotate in their own ceremonies,
 *  and `recovery_kit` must never be re-evaluated under an unchanged
 *  supersecret (§16.9). */
export const ORG_ROTATED_KEY_IDS = ['attr_encrypt', 'msg_encrypt'] as const;

const HYBRID_KEM = 'X25519-MLKem1024';

/**
 * Build the org bag a rotation submits: the two new generations, plus EVERY
 * entry the server bag already held — the rotated ids marked `revoked` (kept
 * for chain-walking historical claims, WP §3.4.8), everything else verbatim.
 *
 * `serverBag` is the DECRYPTED `GET /api/organization/{orgCid}/keybag`, never
 * a bag reassembled from wrapped copies (G10, above).
 *
 * Throws rather than emitting two entries with the same `(keyId, keyIndex)`:
 * the bag's unique constraint is `(accountid, keyid, keyindex)`, and the
 * collision would only surface at the database — after the whole org bag had
 * already been deleted inside the same transaction.
 */
export function buildRotatedOrgBag(
    serverBag: KeyBag,
    newAttrPrivateKeyB64: string,
    newMsgPrivateKeyB64: string,
    newAttrIndex: number,
    newMsgIndex: number,
): KeyBag {
    const rotated = new Set<string>(ORG_ROTATED_KEY_IDS);

    const entries: KeyBagEntry[] = [
        {
            keyId: 'attr_encrypt', algorithm: HYBRID_KEM,
            privateKey: newAttrPrivateKeyB64, keyIndex: newAttrIndex, status: 'active',
        },
        {
            keyId: 'msg_encrypt', algorithm: HYBRID_KEM,
            privateKey: newMsgPrivateKeyB64, keyIndex: newMsgIndex, status: 'active',
        },
        ...serverBag.entries.map(e => (
            rotated.has(e.keyId) ? { ...e, status: 'revoked' as const } : { ...e }
        )),
    ];

    const seen = new Set<string>();
    for (const e of entries) {
        const slot = `${e.keyId}#${e.keyIndex ?? 0}`;
        if (seen.has(slot)) {
            throw new Error(
                `The organization key bag already holds ${slot} — the rotation would replace it with a `
                + 'new key at the same generation. Reload the workspace and try again.');
        }
        seen.add(slot);
    }
    return { entries };
}

/** One wrap a rotation submits, before it is wrapped to the delegate's key. */
export interface PlannedWrap {
    delegateCid: string;
    keyId: string;
    keyIndex: number;
    /** The organization's private key of this generation, base64. */
    privateKey: string;
}

/**
 * The wrapped set a rotation submits (docs/cid-governance.md M15): for every
 * delegate, every entry of the organization's FULL bag — the rotated one, so the
 * new generations and the old ones kept for chain-walking — that the delegate's
 * roles entitle it to. Planned from `bag`, never from the rotating delegate's own
 * wrapped copies: an admin holds neither `main_signing_key` nor `xrpl_master` nor
 * `bitcash_wallet`, and a set built from its copies stripped them from the owners
 * and treasurers who hold them (the server now keeps a wrap a rotation does not
 * resubmit, too). The recovery set is wrapped to nobody (NEVER_DELEGATED_KEY_TYPES).
 */
export function planRotationWraps(
    bag: KeyBag, delegates: readonly { cid: string; roles: readonly string[] }[],
): PlannedWrap[] {
    const plan: PlannedWrap[] = [];
    for (const delegate of delegates) {
        const entitled = getEntitledOrgKeys([...delegate.roles]);
        for (const entry of bag.entries) {
            if (!entitled.has(entry.keyId)) continue;
            plan.push({
                delegateCid: delegate.cid,
                keyId: entry.keyId,
                keyIndex: entry.keyIndex ?? 0,
                privateKey: entry.privateKey,
            });
        }
    }
    return plan;
}
